<template>
    <div class="school-questionnaire" :class="{ 'registration-step': isRegistrationStep }">
        <div class="questionnaire-container">
            <!-- Header with back button -->
            <div class="questionnaire-header">
                <v-btn
                    v-if="currentStep > 1"
                    icon
                    @click="prevStep"
                    class="back-btn"
                >
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <router-link
                    v-else
                    :to="{ name: 'HousingLanding', params: { schoolSlug } }"
                    class="back-link"
                >
                    <v-icon>mdi-arrow-left</v-icon>
                </router-link>
            </div>

            <!-- Progress bar -->
            <div class="progress-container">
                <div class="progress-bar">
                    <div
                        class="progress-fill"
                        :style="{ width: progressPercent + '%' }"
                    ></div>
                </div>
                <span class="progress-text">{{ currentStep }} of {{ totalSteps }}</span>
            </div>

            <!-- Step content -->
            <div class="step-content">
                <component
                    :is="currentStepComponent"
                    :school-slug="schoolSlug"
                    @next="nextStep"
                    @prev="prevStep"
                />
            </div>
        </div>
    </div>
</template>

<script>
import HousingTypeStep from '@/components/Questionnaire/steps/HousingTypeStep.vue';
import SchoolStep from '@/components/Questionnaire/steps/SchoolStep.vue';
import TransportationStep from '@/components/Questionnaire/steps/TransportationStep.vue';
import PetsStep from '@/components/Questionnaire/steps/PetsStep.vue';
import MoveInDateStep from '@/components/Questionnaire/steps/MoveInDateStep.vue';
import LeaseLengthStep from '@/components/Questionnaire/steps/LeaseLengthStep.vue';
import CosignersStep from '@/components/Questionnaire/steps/CosignersStep.vue';
import EvictionStep from '@/components/Questionnaire/steps/EvictionStep.vue';
import RegistrationStep from '@/components/Questionnaire/steps/RegistrationStep.vue';

export default {
    name: 'SchoolQuestionnaire',

    components: {
        HousingTypeStep,
        SchoolStep,
        TransportationStep,
        PetsStep,
        MoveInDateStep,
        LeaseLengthStep,
        CosignersStep,
        EvictionStep,
        RegistrationStep
    },

    props: {
        schoolSlug: {
            type: String,
            required: true
        }
    },

    computed: {
        currentStep() {
            return this.$store.getters['Questionnaire/getCurrentStep'];
        },
        totalSteps() {
            return this.$store.getters['Questionnaire/getTotalSteps'];
        },
        progressPercent() {
            return (this.currentStep / this.totalSteps) * 100;
        },
        currentStepComponent() {
            const steps = {
                1: 'HousingTypeStep',
                2: 'SchoolStep',
                3: 'TransportationStep',
                4: 'PetsStep',
                5: 'MoveInDateStep',
                6: 'LeaseLengthStep',
                7: 'CosignersStep',
                8: 'EvictionStep',
                9: 'RegistrationStep'
            };
            return steps[this.currentStep] || 'HousingTypeStep';
        },
        isRegistrationStep() {
            return this.currentStep === 9;
        }
    },

    created() {
        // Redirect logged-in users to properties
        if (this.$store.getters.isUserLoggedIn) {
            this.$router.push('/properties');
            return;
        }
        this.$store.dispatch('Questionnaire/initQuestionnaire', this.schoolSlug);
    },

    methods: {
        nextStep() {
            this.$store.dispatch('Questionnaire/nextStep');
        },
        prevStep() {
            this.$store.dispatch('Questionnaire/prevStep');
        }
    }
};
</script>

<style scoped>
.school-questionnaire {
    min-height: 100vh;
    background: #f5f5f5;
    display: flex;
    justify-content: center;
    padding: 20px;
}

.questionnaire-container {
    width: 100%;
    max-width: 600px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    padding: 24px;
    min-height: calc(100vh - 40px);
    display: flex;
    flex-direction: column;
    position: relative;
}

.questionnaire-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}

.back-btn,
.back-link {
    color: #333;
}

.progress-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 32px;
}

.progress-bar {
    flex: 1;
    height: 6px;
    background: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #2D76E3, #4DD0E1);
    border-radius: 3px;
    transition: width 0.3s ease;
}

.progress-text {
    font-size: 14px;
    color: #666;
    white-space: nowrap;
    font-weight: 500;
}

.step-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-bottom: 20px;
}

/* Deep styles for step components - Desktop */
::v-deep .step-footer {
    margin-top: auto;
    padding-top: 32px;
}

::v-deep .next-btn {
    font-weight: 600;
    text-transform: none;
    font-size: 16px;
    border-radius: 12px;
    letter-spacing: 0.3px;
    box-shadow: 0 4px 14px rgba(45, 118, 227, 0.4);
    transition: all 0.2s ease;
}

::v-deep .next-btn:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(45, 118, 227, 0.5);
    transform: translateY(-1px);
}

::v-deep .next-btn:disabled {
    box-shadow: none;
    opacity: 0.6;
}

/* Mobile styles */
@media (max-width: 600px) {
    .school-questionnaire {
        padding: 0;
        background: #f5f5f5;
    }

    .questionnaire-container {
        border-radius: 0;
        min-height: 100vh;
        padding: 16px;
        padding-bottom: 16px;
        box-shadow: none;
        display: flex;
        flex-direction: column;
    }

    .step-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding-bottom: 0;
    }

    /* Sticky footer at bottom of container on mobile */
    ::v-deep .step-footer {
        position: sticky;
        bottom: 0;
        padding: 16px 0;
        padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
        background: white;
        margin-top: auto;
        margin-left: -16px;
        margin-right: -16px;
        padding-left: 16px;
        padding-right: 16px;
        box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
    }

    ::v-deep .next-btn {
        height: 54px !important;
        font-size: 17px;
        border-radius: 14px;
    }

    /* Hide any login prompt on mobile to keep footer clean */
    ::v-deep .login-prompt {
        display: none;
    }

    /* Registration step - no sticky footer */
    .school-questionnaire.registration-step ::v-deep .step-footer {
        position: static;
        margin-left: 0;
        margin-right: 0;
        padding-left: 0;
        padding-right: 0;
        box-shadow: none;
        padding-bottom: 16px;
    }
}

/* Tablet and small desktop */
@media (min-width: 601px) and (max-width: 960px) {
    .questionnaire-container {
        max-width: 540px;
        padding: 32px;
    }

    ::v-deep .next-btn {
        height: 52px !important;
    }
}

/* Large desktop */
@media (min-width: 961px) {
    .school-questionnaire {
        padding: 40px 20px;
        align-items: flex-start;
        padding-top: 60px;
    }

    .questionnaire-container {
        min-height: auto;
        padding: 40px 48px;
        border-radius: 20px;
        box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
    }

    ::v-deep .next-btn {
        height: 54px !important;
        max-width: 320px;
        margin: 0 auto;
        display: block;
    }

    ::v-deep .step-footer {
        padding-top: 40px;
        text-align: center;
    }
}
</style>
